import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  CircularProgress,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoginRequest } from '../types/api';
import { authService } from '../services/auth-service';
import { useAuthStore } from '../stores/auth-store';
import { httpClient } from '../services/http-client';
import { AuthHeader } from '../components/AuthHeader';
import { TwoFactorChallenge } from '../components/security/TwoFactorChallenge';
import { isTwoFactorChallenge } from '../services/twofactor-service';

interface LoginPageProps {
  onNavigateToRegister?: () => void;
  onLoginSuccess?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({
  onNavigateToRegister,
  onLoginSuccess,
}) => {
  const [formData, setFormData] = useState<LoginRequest>({
    email: '',
    password: '',
  });
  
  const { login, setLoading, setError, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);

  // Set when the password was right but the account demands a second factor.
  // While this is non-null the password form is REPLACED, not merely
  // supplemented: there is no session yet, and the challenge is the only thing
  // carrying the attempt forward.
  const [challengeToken, setChallengeToken] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(formData);

      if (isTwoFactorChallenge(response.data)) {
        // No tokens were issued. Hand over to the second step.
        setChallengeToken(response.data.challenge_token);
        return;
      }

      // Set the auth token in the HTTP client
      httpClient.setAuthToken(response.data.access_token);

      // Update auth store
      login(response.data.user, response.data.access_token);

      onLoginSuccess?.();
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <AuthHeader />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            {challengeToken ? (
              <TwoFactorChallenge
                challengeToken={challengeToken}
                onVerified={(auth) => {
                  authService.adoptSession(auth);
                  login(auth.user, auth.access_token);
                  onLoginSuccess?.();
                }}
                onCancel={() => {
                  // Drop the challenge and the password: restarting the flow
                  // must not leave a half-used credential lying around.
                  setChallengeToken(null);
                  setFormData((prev) => ({ ...prev, password: '' }));
                  clearError();
                }}
              />
            ) : (
            <>
            <Typography component="h2" variant="h5" align="center" gutterBottom>
              Sign In
            </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={isLoading}
            />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={formData.password}
            onChange={handleChange}
            disabled={isLoading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
            
            <Box textAlign="center">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={onNavigateToRegister}
                disabled={isLoading}
                sx={{
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Don't have an account? Sign Up
              </Link>
            </Box>
          </Box>
            </>
            )}
        </Paper>
      </Box>
    </Container>
    </Box>
  );
};